import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { permissionsAPI } from '../services/api';
import { Shield, Plus, Edit2, Trash2, Search } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';

const Permissions = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToCreate, goToEdit } = createNavigationHelpers('permissions', navigate);
  const { permissions: permissionPerms } = usePermissions();

  // Fetch permissions
  const { data: permissionsData, isLoading: permissionsLoading, error } = useQuery(
    ['permissions'],
    () => permissionsAPI.getAll(),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Permissions query success:', data);
      },
      onError: (error) => {
        console.error('Permissions fetch error:', error);
        toast.error('Failed to load permissions');
      }
    }
  );

  // Create/Update permission mutation
  const permissionMutation = useMutation(
    (data) => {
      if (editingPermission) {
        return permissionsAPI.update(editingPermission.uuid, data);
      }
      return permissionsAPI.create(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permissions']);
        toast.success(editingPermission ? 'Permission updated successfully!' : 'Permission created successfully!');
        handleClosePermissionModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  // Delete permission mutation
  const deletePermissionMutation = useMutation(
    (uuid) => permissionsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permissions']);
        toast.success('Permission deleted successfully!');
        setDeleteModalOpen(false);
        setPermissionToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleEditPermission = (permission) => {
    goToEdit(permission.uuid);
  };

  const handleDeletePermission = (permission) => {
    setPermissionToDelete(permission);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (permissionToDelete) {
      deletePermissionMutation.mutate(permissionToDelete.uuid);
    }
  };

  

  // Extract data from the correct paths in the API responses
  let permissions = [];
  if (Array.isArray(permissionsData?.data?.data)) {
    permissions = permissionsData.data.data;
  } else if (Array.isArray(permissionsData?.data)) {
    permissions = permissionsData.data;
  }

  // Filter permissions
  const filteredPermissions = permissions.filter(permission => {
    const matchesSearch = permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = moduleFilter === 'all' || permission.module === moduleFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && permission.is_active) ||
                         (statusFilter === 'inactive' && !permission.is_active);
    
    return matchesSearch && matchesModule && matchesStatus;
  });

  // Get unique modules
  const modules = [...new Set(permissions.map(p => p.module))].sort();

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load permissions</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">System Permissions</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage system permissions and access controls
            </p>
          </div>
          {/* Add Permission Button - Only show if user can create permissions */}
          {permissionPerms.canCreate && (
            <button
              onClick={goToCreate}
              className="btn-primary flex items-center justify-center gap-2 text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-4 h-4" />
              Add Permission
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 search-input-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-4 py-2 text-sm sm:text-base"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="input-modern px-3 py-2 text-sm sm:text-base"
            >
              <option value="all">All Modules</option>
              {modules.map((module) => (
                <option key={module} value={module}>
                  {module.charAt(0).toUpperCase() + module.slice(1)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-modern px-3 py-2 text-sm sm:text-base"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>



      {/* Permissions Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {filteredPermissions.map((permission, index) => (
          <div
            key={permission.uuid || permission.id}
            className="card-modern group hover-lift animate-fade-in-up overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white shadow-glow group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <Shield className="h-5 w-5" />
              </div>
              <span
                className={`badge-responsive ${
                  permission.is_active
                    ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                    : 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
                }`}
              >
                {permission.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div className="mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white break-words mb-2">{permission.name}</h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Module:</span> <span className="break-words">{permission.module}</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 break-words">
                {permission.description || 'No description provided'}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span className="truncate">Assigned to: {permission.assigned_employees_count || 0} employees</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Edit Button - Only show if user can update permissions */}
              {permissionPerms.canUpdate && (
                <button
                  onClick={() => handleEditPermission(permission)}
                  className="btn-secondary text-sm flex items-center gap-1 px-3 py-2 rounded-lg"
                >
                  <Edit2 className="h-3 w-3 flex-shrink-0" />
                  Edit
                </button>
              )}
              
              {/* Delete Button - Only show if user can delete permissions */}
              {permissionPerms.canDelete && (
                <button
                  onClick={() => handleDeletePermission(permission)}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                >
                  <Trash2 className="h-3 w-3 flex-shrink-0" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredPermissions.length === 0 && (
        <div className="card-modern text-center py-12 animate-fade-in-up">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white text-2xl mb-4 mx-auto shadow-glow">
            <Shield className="h-8 w-8" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No permissions found</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {searchTerm || moduleFilter !== 'all' || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first permission'
            }
          </p>
          {/* Create Permission Button - Only show if user can create permissions */}
          {permissionPerms.canCreate && (
            <button
              onClick={goToCreate}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Permission
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPermissionToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Permission"
        message={`Are you sure you want to delete the permission "${permissionToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Permissions; 







